import { on } from '@ngrx/store';
import { FormState } from '../../state/form.state';
import { enableManualIndividualization, updateTopicVisibility } from '../../actions/form.actions';
import { FormsConstants } from '../../../types/forms-constants';
import { ManuallyIndividualizedComponents } from '../../../types/individualization-component';
import { Answers } from '../../../types/answers';
import { AnswerUtilities } from '../../../utilities/answer-utilities';
import { isDefined } from '@evorait/pace-core';

export function toggleManualIndividualizationReducer(state: Readonly<FormState>): Readonly<FormState> {
    const { selectedParticipantCopyId, participantCopies } = state;

    const newAnswers: Answers = AnswerUtilities.getCompleteAnswers(
        state.selectedParticipantCopyId,
        state.content,
        state.checkpoints,
        state.participantCopies[state.selectedParticipantCopyId].options,
        state.participantCopies[state.selectedParticipantCopyId].functionalElements,
        state.checkpointDependencies,
        state.typeFilters,
        state.formFilters,
        !state.enableManualIndividualization,
        state.type,
    );

    return {
        ...state,
        enableManualIndividualization: !state.enableManualIndividualization,
        readOnlyReason: state.enableManualIndividualization
            ? FormsConstants.READONLY_REASON.NONE
            : FormsConstants.READONLY_REASON.MANUAL_INDIVIDUALIZATION,
        participantCopies: {
            ...participantCopies,
            [selectedParticipantCopyId]: {
                ...participantCopies[selectedParticipantCopyId],
                answers: newAnswers,
            },
        },
    };
}

export function toggleTopicVisibilityReducer(
    state: Readonly<FormState>,
    props: {
        visibility: boolean;
        id: number;
        materialNumber?: string;
        serialNumber?: string;
        description?: string;
    },
): Readonly<FormState> {
    const parentContent = state.content[props.id];
    if (!parentContent) {
        return state;
    }

    const idsToUpdate = [parentContent.id, ...parentContent.children.map((child) => child.id)];

    const updatedContent = { ...state.content };
    idsToUpdate.forEach((contentId) => {
        if (updatedContent[contentId]) {
            updatedContent[contentId] = {
                ...updatedContent[contentId],
                visible: props.visibility,
            };
        }
    });
    let updatedComponents: ManuallyIndividualizedComponents[] = [];
    if (
        state.individualization &&
        state.manIndividualizationComponents &&
        isDefined(props.materialNumber) &&
        isDefined(props.serialNumber)
    ) {
        const idAlreadyAdded = state.manIndividualizationComponents?.find((component) => component.id === props.id);

        if (idAlreadyAdded) {
            updatedComponents = state.manIndividualizationComponents?.map((component) =>
                component.id === props.id
                    ? {
                          ...component,
                          materialNumber: props.materialNumber,
                          serialNumber: props.serialNumber,
                          explanation: props.description,
                          isAvailable: props.visibility,
                      }
                    : component,
            );
        } else {
            const newManualIndividualizationData = {
                id: props.id,
                materialNumber: props.materialNumber,
                serialNumber: props.serialNumber,
                explanation: props.description,
                isAvailable: props.visibility,
            };

            updatedComponents = [...(state.manIndividualizationComponents ?? []), newManualIndividualizationData];
        }
    }

    return {
        ...state,
        manIndividualizationComponents: state.individualization ? updatedComponents : [],
        content: updatedContent,
    };
}

export const manualIndividualizationReducer = [
    on<FormState, Array<typeof enableManualIndividualization>>(
        enableManualIndividualization,
        toggleManualIndividualizationReducer,
    ),
    on<FormState, Array<typeof updateTopicVisibility>>(updateTopicVisibility, toggleTopicVisibilityReducer),
];
